import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import { useTenantTranslation } from "hooks/formatters";
import {
  LabellingCoverageGranularity,
  useGetTenantLabellingCoverageQuery,
} from "store/api/generatedApi";
import { CoverageSection } from "./CoverageSection";
import { sorted } from "helpers";

interface LocationsTabProps {
  tenantName: string;
  gapTolerance: number;
  granularity: LabellingCoverageGranularity;
}

export const LocationsTab = ({
  tenantName,
  gapTolerance,
  granularity,
}: LocationsTabProps) => {
  const { t } = useTenantTranslation();

  const overviewEndDate = dayjs().endOf("day").toISOString();
  const overviewStartDate = dayjs()
    .subtract(7, "days")
    .startOf("day")
    .toISOString();

  const { data, isLoading, isError } = useGetTenantLabellingCoverageQuery({
    tenantName,
    start: overviewStartDate,
    end: overviewEndDate,
    granularity: granularity,
    gapToleranceSeconds: gapTolerance,
  });

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }
  if (isError || !data) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{t("failedToLoadData")}</Typography>
      </Box>
    );
  }

  const locations = sorted(
    Object.entries(data.coverage_by_location),
    (item) => item[0]
  );

  return (
    <Stack spacing={3}>
      <CoverageSection
        title={t("tenantOverview")}
        buckets={data.buckets}
        summary={data.summary}
      />

      <Typography variant="h6">{t("byLocation")}</Typography>
      <Stack spacing={2}>
        {locations.map(([locationName, locationData]) => (
          <CoverageSection
            key={locationName}
            title={locationName}
            buckets={locationData.buckets}
            summary={locationData.summary}
          />
        ))}
      </Stack>

      {locations.length === 0 && (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography color="text.secondary">
            {t("noLocationsFound")}
          </Typography>
        </Box>
      )}
    </Stack>
  );
};
